import React from "react";
import { formatDate, formatLKR } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Calendar, CheckCircle2, Clock, Info } from "lucide-react";

// ---------------------------------------------------------------------------
// Test 4 Fix 5/6/7 — Dividend timeline now:
//   • Shows three explicit lines per row: Announced / XD Date / Payment
//     so users no longer have to guess what each date means.
//   • Estimated rows show NO dates AND a clearer explanation message
//     telling the user the data comes from TradingView and that XD/Pay
//     dates appear automatically once the company officially declares
//     the dividend on CSE.
//   • Coloured type and subtype badges (Final/Interim/Special/Estimated +
//     Cash/Scrip/Mixed).
// ---------------------------------------------------------------------------

const Row = ({ icon: Icon, label, value, dim }) => (
  <div className="flex items-center gap-2 text-xs">
    <Icon className={`h-3 w-3 shrink-0 ${dim ? "text-muted-foreground/60" : "text-muted-foreground"}`} />
    <span className="text-muted-foreground w-20 shrink-0">{label}</span>
    <span className={dim ? "text-muted-foreground italic" : "text-foreground tabular-nums"}>
      {value}
    </span>
  </div>
);

const DividendTimeline = ({ items = [] }) => {
  if (!items.length) return <div className="text-sm text-muted-foreground">No dividend records.</div>;

  const typeColor = (rawType, isEstimated) => {
    if (isEstimated) return "bg-muted text-muted-foreground border-border";
    const t = (rawType || "").toLowerCase();
    if (t === "interim") return "bg-[hsl(var(--chart-2))]/15 text-[hsl(var(--chart-2))] border-[hsl(var(--chart-2))]/30";
    if (t === "special") return "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30";
    return "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/30";
  };

  const subtypeLabel = (sub) => {
    const s = (sub || "cash").toLowerCase();
    if (s === "scrip") return { label: "Scrip", tip: "Bonus shares — no cash to holder. Excluded from yield." };
    if (s === "mixed") return { label: "Mixed", tip: "Cash + scrip combo. Yield based on cash component only." };
    return { label: "Cash", tip: "Cash dividend paid to holders." };
  };

  return (
    <ul className="space-y-5" data-testid="dividends-timeline">
      {items.map((d, idx) => {
        const isEstimated = d.is_estimated || (d.dividend_type || "").toLowerCase() === "estimated";
        const sub = subtypeLabel(d.dividend_subtype);
        // Test 4 Fix 5 — read both ex_dividend_date (legacy) and xd_date
        // (CSE sync) so timeline never misses XD when only one is set.
        const xd = isEstimated ? null : (d.xd_date || d.ex_dividend_date);
        const pay = isEstimated ? null : d.payment_date;
        const ann = isEstimated ? null : d.announcement_date;
        const dpsValue = d.dividend_per_share ?? d.dps ?? d.amount_per_share;
        return (
          <li
            key={idx}
            data-testid="dividend-timeline-item"
            className="relative pl-6"
          >
            <span
              className={`absolute left-0 top-1.5 h-2.5 w-2.5 rounded-full ${
                isEstimated ? "bg-muted-foreground/50" : "bg-primary"
              }`}
            />
            {idx < items.length - 1 && (
              <span className="absolute left-[5px] top-4 bottom-[-20px] w-px bg-border" />
            )}
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0 space-y-1">
                {/* Test 4 Fix 5 — three explicit dates */}
                {isEstimated ? (
                  <div
                    className="rounded-md border border-dashed border-border bg-muted/30 px-2.5 py-2 text-[11px] text-muted-foreground leading-snug max-w-md"
                    data-testid={`timeline-estimated-note-${idx}`}
                  >
                    <span className="inline-flex items-center gap-1.5 font-medium text-foreground/80 mb-0.5">
                      <Info className="h-3 w-3" />
                      Estimated from TradingView annual DPS
                    </span>
                    <p>
                      XD and payment dates will appear automatically when
                      the company officially declares the dividend on
                      CSE — usually 4–6 weeks before the XD date.
                    </p>
                  </div>
                ) : (
                  <>
                    <Row
                      icon={Calendar}
                      label="Announced"
                      value={ann ? formatDate(ann) : "—"}
                      dim={!ann}
                    />
                    <Row
                      icon={Clock}
                      label="XD Date"
                      value={xd ? formatDate(xd) : "—"}
                      dim={!xd}
                    />
                    <Row
                      icon={CheckCircle2}
                      label="Payment"
                      value={pay ? formatDate(pay) : "—"}
                      dim={!pay}
                    />
                  </>
                )}
              </div>
              <div className="flex items-center gap-2 flex-wrap justify-end">
                <Badge
                  variant="outline"
                  className={`capitalize border ${typeColor(d.dividend_type, isEstimated)}`}
                  data-testid={`timeline-type-${idx}`}
                >
                  {isEstimated ? "Estimated" : (d.dividend_type || "declared")}
                </Badge>
                {!isEstimated && (
                  <Badge
                    variant="outline"
                    className={`text-[10px] px-1.5 py-0 border ${
                      d.dividend_subtype === "scrip"
                        ? "bg-muted text-muted-foreground border-border"
                        : d.dividend_subtype === "mixed"
                          ? "bg-[hsl(var(--chart-3))]/15 text-[hsl(var(--chart-3))] border-[hsl(var(--chart-3))]/30"
                          : "bg-background text-muted-foreground border-border"
                    }`}
                    title={sub.tip}
                    data-testid={`timeline-subtype-${idx}`}
                  >
                    {sub.label}
                  </Badge>
                )}
                <div className="text-base font-semibold tabular" data-testid={`timeline-dps-${idx}`}>
                  {d.dividend_subtype === "scrip"
                    ? <span className="text-muted-foreground">— (bonus)</span>
                    : (dpsValue != null ? formatLKR(dpsValue) : "—")}
                </div>
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
};

export default DividendTimeline;
